import React, { useEffect, useMemo, useState } from 'react';

// INTERNAL Knowledge Consensus Engine — sits ON TOP of the Knowledge Objects.
// It creates no facts; it measures how strongly the Knowledge Corpus AGREES on
// each operational concept (a Consensus Group). Rendered as a subtab inside the
// Learning Engine. Not customer-facing. Consensus is a statistical view only —
// Knowledge Objects remain the source of truth, and every recommendation
// candidate is fully traceable back to the documents that produced it.
const API = 'https://tm-audit-platform-production.up.railway.app';

const fmtNum = (n) => (n || 0).toLocaleString();
const fmtPct = (n) => (n == null ? '—' : Math.round(n * 100) + '%');
const fmtDate = (d) => d ? new Date(d + (String(d).length === 10 ? 'T00:00:00Z' : '')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—';

const READINESS_COLOR = {
    'Ready': 'var(--green)',
    'Needs More Evidence': 'var(--amber)',
    'Conflicted': 'var(--red, #c0392b)',
    'Historical': 'var(--text-muted)',
    'Deprecated': 'var(--text-muted)',
};
const STANCE_COLOR = {
    'Agreement': 'var(--green)',
    'Conflict': 'var(--red, #c0392b)',
    'Superseded': 'var(--text-muted)',
    'Historical': 'var(--text-muted)',
};
const EVENT_COLOR = {
    'Introduced': 'var(--blue)', 'Reaffirmed': 'var(--green)', 'Changed': 'var(--amber)', 'Superseded': 'var(--text-muted)',
};

function Badge({ text, color }) {
    return <span className="le-badge" style={{ color, borderColor: color }}>{text}</span>;
}

function KpiCard({ label, value, accent }) {
    return (
        <div className="gd-kpi">
            <div className="gd-kpi-label">{label}</div>
            <div className="gd-kpi-value" style={accent ? { color: accent } : undefined}>{value}</div>
        </div>
    );
}

// Horizontal agreement meter for a group's value distribution.
function ConsensusBar({ distribution, dominant }) {
    if (!distribution || !distribution.length) return null;
    return (
        <div className="kc-bar">
            {distribution.map(d => (
                <div key={d.bucket} className="kc-bar-seg"
                    title={`${d.label}: ${Math.round(d.share * 100)}% (${d.count})`}
                    style={{ width: `${Math.max(3, d.share * 100)}%`, background: d.bucket === dominant ? 'var(--green)' : 'var(--red, #c0392b)' }}>
                    {d.share >= 0.12 ? `${d.label} ${Math.round(d.share * 100)}%` : ''}
                </div>
            ))}
        </div>
    );
}

export default function KnowledgeConsensus({ session }) {
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const [readinessFilter, setReadinessFilter] = useState('all');
    const [selectedKey, setSelectedKey] = useState(null);

    const headers = useMemo(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`,
    }), [session]);

    const getJSON = async (path) => {
        const res = await fetch(API + path, { headers });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
        return res.json();
    };

    const load = async () => {
        setLoading(true); setErr(null);
        try { setDashboard(await getJSON('/api/admin/consensus/dashboard')); }
        catch (e) { setErr(e.message); }
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-line */ }, []);

    const recompute = async () => {
        setBusy(true); setMsg(null);
        try {
            const res = await fetch(API + '/api/admin/consensus/recompute', { method: 'POST', headers, body: JSON.stringify({}) });
            const out = await res.json();
            if (!res.ok || !out.success) throw new Error(out.error || `HTTP ${res.status}`);
            const r = out.byReadiness || {};
            setMsg(`✓ ${out.memberships} membership(s) across ${out.scored} group(s) — ${r['Ready'] || 0} ready · ${r['Conflicted'] || 0} conflicted · ${r['Needs More Evidence'] || 0} need evidence.`);
            await load();
        } catch (e) { setMsg('⚠ ' + e.message); }
        setBusy(false);
    };

    const groups = useMemo(() => {
        const list = dashboard?.groups || [];
        if (readinessFilter === 'all') return list;
        return list.filter(g => g.readiness === readinessFilter);
    }, [dashboard, readinessFilter]);

    return (
        <div>
            <div className="le-note">
                Knowledge Consensus Engine — sits on top of the Knowledge Objects. It creates <b>no facts</b>; it measures how strongly the Knowledge Corpus <b>agrees</b> on each operational concept (a <b>Consensus Group</b>: Payment Terms, Daily Moisture Readings, Equipment Documentation, …). Knowledge Objects remain the source of truth — consensus is a statistical view, and every recommendation is fully traceable back to the documents that produced it. Infrastructure only: it builds no production rules and changes no audit behavior.
            </div>

            <div className="kc-pipeline le-pipeline">
                {(dashboard?.pipeline || ['Knowledge Corpus', 'Knowledge Extraction', 'Knowledge Objects', 'Knowledge Quality', 'Knowledge Consensus', 'Recommendations', 'Audit Intelligence']).map((s, i, a) => (
                    <React.Fragment key={s}>
                        <span className={'le-pipe-step' + (s === 'Knowledge Consensus' ? ' kc-pipe-active' : '')}>{s}</span>
                        {i < a.length - 1 && <span className="le-pipe-arrow">→</span>}
                    </React.Fragment>
                ))}
            </div>

            <div className="le-upload">
                <button className={'le-btn le-approve' + (busy ? ' le-disabled' : '')} disabled={busy} onClick={recompute}>
                    {busy ? 'Recomputing…' : '⚖ Recompute Consensus'}
                </button>
                <button className="history-back-link" onClick={load}>↻ Refresh</button>
                {msg && <span className={msg.startsWith('⚠') ? 'le-error' : 'le-upload-ok'} style={{ margin: 0 }}>{msg}</span>}
            </div>

            {err && <div className="le-error">⚠ {err}</div>}
            {loading && <div className="history-loading"><div className="spinner" /></div>}

            {!loading && dashboard && (
                <div>
                    <div className="gd-section-title">Consensus Overview</div>
                    <div className="gd-kpi-grid">
                        <KpiCard label="Concepts" value={fmtNum(dashboard.cards.totalGroups)} />
                        <KpiCard label="Ready" value={fmtNum(dashboard.cards.ready)} accent="var(--green)" />
                        <KpiCard label="Needs More Evidence" value={fmtNum(dashboard.cards.needsMore)} accent="var(--amber)" />
                        <KpiCard label="Conflicted" value={fmtNum(dashboard.cards.conflicted)} accent="var(--red, #c0392b)" />
                        <KpiCard label="Historical" value={fmtNum(dashboard.cards.historical)} />
                        <KpiCard label="Total Evidence" value={fmtNum(dashboard.cards.totalEvidence)} />
                    </div>

                    <div className="le-controls">
                        <button className={'le-pill ' + (readinessFilter === 'all' ? 'active' : '')} onClick={() => setReadinessFilter('all')}>All</button>
                        {(dashboard.readinessCatalog || []).map(r => (
                            <button key={r} className={'le-pill ' + (readinessFilter === r ? 'active' : '')} onClick={() => setReadinessFilter(r)}>{r}</button>
                        ))}
                        <span className="le-count">{groups.length} concept(s)</span>
                    </div>

                    {dashboard.cards.totalGroups === 0 && <div className="le-empty">No Consensus Groups yet. Click “Recompute Consensus” to derive them from the Knowledge Objects.</div>}

                    <div className="kc-grid">
                        {groups.map(g => (
                            <div key={g.concept_key} className="le-kb-card le-clickable" onClick={() => setSelectedKey(g.concept_key)}>
                                <div className="le-kb-head">
                                    <Badge text={g.readiness || '—'} color={READINESS_COLOR[g.readiness] || 'var(--text-muted)'} />
                                    {g.category && <Badge text={g.category} color="var(--blue)" />}
                                </div>
                                <div className="le-kb-title">{g.label}</div>
                                <div className="kc-score-row">
                                    <span className="kc-score" style={{ color: READINESS_COLOR[g.readiness] }}>{fmtPct(g.agreement_score)}</span>
                                    <span className="kc-score-label">consensus{g.consensus_value_label ? ` · ${g.consensus_value_label}` : ''}</span>
                                </div>
                                <div className="le-kb-foot">
                                    {fmtNum(g.evidence_count)} fact(s) · {fmtNum(g.unique_source_count)} source(s)
                                    {g.unique_servpro_bulletins ? ` · ${g.unique_servpro_bulletins} bulletin(s)` : ''}
                                    {g.contradiction_score > 0 ? ` · ${fmtPct(g.contradiction_score)} conflict` : ''}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {selectedKey && (
                <ConsensusGroupDetail conceptKey={selectedKey} headers={headers} onClose={() => setSelectedKey(null)} />
            )}
        </div>
    );
}

// ── Consensus Group detail drawer (Parts 4 / 5 / 6 / 9) ─────────────────────
function ConsensusGroupDetail({ conceptKey, headers, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let live = true;
        (async () => {
            setLoading(true);
            try {
                const res = await fetch(API + '/api/admin/consensus/group/' + encodeURIComponent(conceptKey), { headers });
                const out = await res.json();
                if (live) setData(out);
            } catch (e) { if (live) setData({ error: e.message }); }
            if (live) setLoading(false);
        })();
        return () => { live = false; };
    }, [conceptKey, headers]);

    const g = data?.group;
    const rec = data?.currentRecommendation;
    return (
        <div className="le-drawer-overlay" onClick={onClose}>
            <div className="le-drawer" onClick={e => e.stopPropagation()}>
                <div className="le-drawer-head"><button className="history-back-link" onClick={onClose}>✕ Close</button></div>
                {loading && <div className="history-loading"><div className="spinner" /></div>}
                {data?.error && <div className="le-error">⚠ {data.error}</div>}
                {!loading && g && (
                    <div className="le-drawer-body">
                        <div className="le-drawer-title">
                            {g.label} <Badge text={g.readiness} color={READINESS_COLOR[g.readiness]} />
                        </div>
                        {g.description && <div className="le-note" style={{ marginTop: 0 }}>{g.description}</div>}

                        {/* Part 7 — consensus score + recommendation readiness */}
                        <div className="kc-headline">
                            <span className="kc-score-big" style={{ color: READINESS_COLOR[g.readiness] }}>{fmtPct(g.agreement_score)}</span>
                            <span className="kc-score-sub">
                                consensus on <b>{g.consensus_value_label || '—'}</b> · {fmtPct(g.contradiction_score)} contradiction
                            </span>
                        </div>
                        <ConsensusBar distribution={data.distribution} dominant={g.consensus_value} />

                        <div className="le-meta-grid" style={{ marginTop: 12 }}>
                            <div><span>Evidence Count</span><b>{fmtNum(g.evidence_count)}</b></div>
                            <div><span>Unique Sources</span><b>{fmtNum(g.unique_source_count)}</b></div>
                            <div><span>Unique Accounts</span><b>{fmtNum(g.unique_accounts)}</b></div>
                            <div><span>Unique Carriers</span><b>{fmtNum(g.unique_carriers)}</b></div>
                            <div><span>SERVPRO Bulletins</span><b>{fmtNum(g.unique_servpro_bulletins)}</b></div>
                            <div><span>Document Families</span><b>{fmtNum(g.unique_document_families)}</b></div>
                            <div><span>Most Recent Revision</span><b>{g.most_recent_revision ? fmtDate(g.most_recent_revision) : '—'}</b></div>
                            <div><span>Avg Confidence</span><b>{fmtPct(g.avg_confidence)}</b></div>
                            <div><span>Avg Quality</span><b>{fmtPct(g.avg_quality_score)}</b></div>
                        </div>

                        {/* Part 9 — explainability (a recommendation is never shown without it) */}
                        {rec && rec.explainability && (
                            <div className="kc-explain">
                                <div className="gd-section-title">Recommendation Readiness</div>
                                <div className="kc-explain-head">
                                    <Badge text={rec.readiness} color={READINESS_COLOR[rec.readiness]} />
                                    {rec.label && <span> Current recommendation: <b>{rec.label}</b> ({fmtPct(rec.agreement_score)})</span>}
                                </div>
                                <div className="kc-explain-body">
                                    This recommendation is supported by:
                                    <ul className="kc-explain-list">
                                        {(rec.explainability.supportedBy || []).map(s => <li key={s.label}>• {fmtNum(s.count)} {s.label}</li>)}
                                        {(rec.explainability.supportedBy || []).length === 0 && <li>• (no supporting sources yet)</li>}
                                    </ul>
                                    {rec.explainability.lastUpdatedLabel && <div className="kc-explain-foot">Last Updated: {rec.explainability.lastUpdatedLabel}</div>}
                                </div>
                            </div>
                        )}

                        {/* Part 5 — contradiction detection */}
                        {(data.contradictions || []).length > 0 && (
                            <>
                                <div className="gd-section-title">Contradictions ({data.contradictions.length})</div>
                                <div className="kc-contradictions">
                                    {data.contradictions.map((c, i) => (
                                        <div key={i} className="kc-contradiction">
                                            <Badge text={c.type} color={c.type === 'Conflict' ? 'var(--red, #c0392b)' : 'var(--amber)'} />
                                            {c.type === 'Conflict'
                                                ? <span> <b>{c.a.label}</b> ({fmtPct(c.a_share)}) vs <b>{c.b.label}</b> ({fmtPct(c.b_share)})</span>
                                                : <span> <b>{c.from.label}</b> (v{c.from.version}) → <b>{c.to.label}</b> (v{c.to.version})</span>}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Part 6 — consensus timeline: Document → Knowledge Object → Consensus Change */}
                        {(data.timeline || []).length > 0 && (
                            <>
                                <div className="gd-section-title">Consensus Timeline ({data.timeline.length})</div>
                                <div className="kc-timeline">
                                    {data.timeline.map((t, i) => (
                                        <div key={i} className={'kc-tl-row' + (t.is_current ? '' : ' kc-tl-historical')}>
                                            <span className="kc-tl-date">{t.date ? fmtDate(t.date) : '—'}</span>
                                            <Badge text={t.event} color={EVENT_COLOR[t.event] || 'var(--text-muted)'} />
                                            <span className="kc-tl-value"><b>{t.value_label}</b></span>
                                            <span className="kc-tl-doc">
                                                {[t.document.issuer, t.document.account, t.document.carrier, t.document.bulletin_code].filter(Boolean).join(' · ') || t.document.document_family || '—'}
                                                {t.object_version > 1 ? ` · v${t.object_version}` : ''}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Part 4 — supporting / conflicting / historical Knowledge Objects (source of truth) */}
                        <ObjectGroup title="Supporting Sources" rows={data.supporting} stance="Agreement" />
                        <ObjectGroup title="Conflicting Sources" rows={data.conflicting} stance="Conflict" />
                        <ObjectGroup title="Historical Versions (preserved)" rows={data.historical} stance="Superseded" />
                    </div>
                )}
            </div>
        </div>
    );
}

function ObjectGroup({ title, rows, stance }) {
    if (!rows || !rows.length) return null;
    return (
        <>
            <div className="gd-section-title">{title} ({rows.length})</div>
            <div className="le-kb-grid">
                {rows.map(m => {
                    const ko = m.knowledge_object || {};
                    return (
                        <div key={m.id} className="le-kb-card">
                            <div className="le-kb-head">
                                <Badge text={m.value_label || m.value_bucket} color={STANCE_COLOR[m.stance] || 'var(--blue)'} />
                                <Badge text={m.stance || stance} color={STANCE_COLOR[m.stance || stance]} />
                                {m.object_version > 1 && <span className="le-vtag">v{m.object_version}</span>}
                            </div>
                            <div className="le-kb-title">{ko.title || m.value_label}</div>
                            <div className="le-kb-body">{ko.description || '—'}</div>
                            {ko.supporting_evidence?.citation && <div className="kc-cite">“{ko.supporting_evidence.citation}”</div>}
                            <div className="le-kb-foot">
                                {[m.issuer, m.account, m.carrier, m.bulletin_code].filter(Boolean).join(' · ') || m.document_family || '—'}
                                {m.effective_date ? ` · eff ${fmtDate(m.effective_date)}` : ''}
                                {' · match '}{fmtPct(m.match_confidence)}
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
}
